/**
 * 一覧データからファセット（タグ等）のユニーク値を取り出す共通ヘルパ。
 * ListView（一覧の絞り込み生成）と SidebarFacets（詳細ページのタグリンク生成）で共用し、
 * 抽出ロジックを一本化する（＝二重実装しない）。
 *
 * sort=true で五十音・ABC順（localeCompare 'ja'）に確定ソートする。タグ一覧は一覧・詳細で
 * 走査元の並び（一覧＝日付降順ソート済み this.all／詳細＝ListLoaderの年ファイル結合順）が
 * 異なり初出順がズレるため、タグ用途では sort を有効にして両ページの並びを一致させる。
 *
 * priority を渡すと、そこに列挙した値を記載順で先頭固定し、残りは ja順で後ろに並べる
 * （data-*-order 属性で「特定タグだけ順序を決めたい」用途。指定したがデータに無い値は無視）。
 *
 * @param {Array<Object>} items 一覧アイテム
 * @param {string} field フィールド名（配列フィールド=tags 等はフラット化）
 * @param {Object} [opts]
 * @param {boolean} [opts.sort=false] 五十音・ABC順に確定ソートするか（既定は出現順）
 * @param {string[]} [opts.priority=[]] 先頭固定する値（記載順）。残りは ja順
 * @returns {string[]} ユニーク値（空値除外）
 */
export function uniqueValues(items, field, { sort = false, priority = [] } = {}) {
  const out = [];
  (items || []).forEach((it) => {
    const iv = it[field];
    (Array.isArray(iv) ? iv : [iv]).forEach((v) => {
      if (v && !out.includes(v)) {
        out.push(v);
      }
    });
  });
  const hasPriority = Array.isArray(priority) && priority.length > 0;
  // priority指定時は「残り」を ja順にするため sort有無に関わらず一旦 ja順に整える
  if (sort || hasPriority) {
    out.sort((a, b) => String(a).localeCompare(String(b), 'ja'));
  }
  if (hasPriority) {
    const pinned = priority.filter((p, i) => priority.indexOf(p) === i && out.includes(p));
    const rest = out.filter((v) => !pinned.includes(v));
    return pinned.concat(rest);
  }
  return out;
}

/**
 * data-*-order 属性（カンマ区切り）を priority 配列にパースする。
 * 前後空白トリム・空要素除外。
 *
 * @param {string} str 例 "WEB広告, CV改善"
 * @returns {string[]}
 */
export function parseOrderList(str) {
  return String(str || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

/**
 * ある値を持つアイテム件数を返す（配列フィールドも対応）。カテゴリ一覧の (件数) 表示用。
 * ListView.buildCats（一覧）と SidebarFacets（詳細）で共用。
 *
 * @param {Array<Object>} items 一覧アイテム
 * @param {string} field フィールド名
 * @param {string} value 対象値
 * @returns {number}
 */
export function valueCount(items, field, value) {
  return (items || []).filter((it) => {
    const iv = it[field];
    return Array.isArray(iv) ? iv.includes(value) : iv === value;
  }).length;
}
